import type { FC, ReactNode } from 'react';
import { lazy } from 'react';
import { useParams } from 'react-router-dom';
import urltemplate from 'url-template';
import { useMemoOne } from 'use-memo-one';
import type {
  ContractManagementResponse,
  FinancialSecuritiesParams,
  RouteContractType,
  RouteParams,
  SpecificContract,
} from 'src/apis/contract-management/types';
import { useContractFinancialSecurities } from 'src/apis/contract-management/use-contract-financial-securities';
import { useContractManagement } from 'src/apis/contract-management/use-contract-management';
import { useDect } from 'src/apis/contract-management/use-dect';
import { useFmct } from 'src/apis/contract-management/use-fmct';
import { useFmctOffer } from 'src/apis/contract-management/use-fmct-offer';
import { useOptionalCreditGraph } from 'src/apis/contract-management/use-get-credit-graph';
import { useOptionalShortTermCreditLimitSummary } from 'src/apis/contract-management/use-short-term-credit-limit-summary';
import { PageSpinner } from 'src/components/spinner-container';
import { useTitle } from 'src/hooks/use-title';
import { useAssignmentsDetailsPageParams } from 'src/pages/operators/assignments/details/financial-securities/use-page-params';
import { useDectOffer } from 'src/pages/transport/secondary-trading/trade/use-dect-offer';
import { useLect } from 'src/pages/transport/secondary-trading/trade/use-lect';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

export const ShippersContractDetails: FC = () => {
  useTitle('Contract Details');

  const { contractId, contractType } = useParams<RouteParams>();

  const contractManagement = useContractManagement();

  if (!contractManagement.response) return <PageSpinner />;

  return (
    <LoadContract
      contractId={contractId}
      contractType={contractType}
      contractManagement={contractManagement.response.data}
    >
      {(contract) => <LoadRest contract={contract} />}
    </LoadContract>
  );
};

const LoadContract: FC<{
  contractId: string;
  contractType: RouteContractType;
  contractManagement: ContractManagementResponse;
  children: (contract: SpecificContract) => ReactNode;
}> = ({ contractId, contractType, contractManagement, children }) => {
  switch (contractType) {
    case 'fmct':
      if (!contractManagement._links.getFmct) throw new Forbidden();
      return (
        <LoadFmct
          url={urltemplate
            .parse(contractManagement._links.getFmct.href)
            .expand({ fmctId: contractId })}
        >
          {children}
        </LoadFmct>
      );
    case 'dect':
      if (!contractManagement._links.getDect) throw new Forbidden();
      return (
        <LoadDect
          url={urltemplate
            .parse(contractManagement._links.getDect.href)
            .expand({ dectId: contractId })}
        >
          {children}
        </LoadDect>
      );
    case 'lect':
      if (!contractManagement._links.getLect) throw new Forbidden();
      return (
        <LoadLect
          url={urltemplate
            .parse(contractManagement._links.getLect.href)
            .expand({ lectId: contractId })}
        >
          {children}
        </LoadLect>
      );
    case 'fmct-offer':
      if (!contractManagement._links.getFmctOffer) throw new Forbidden();
      return (
        <LoadFmctOffer
          url={urltemplate
            .parse(contractManagement._links.getFmctOffer.href)
            .expand({ id: contractId })}
        >
          {children}
        </LoadFmctOffer>
      );
    case 'dect-offer':
      if (!contractManagement._links.getDectOffer) throw new Forbidden();
      return (
        <LoadDectOffer
          url={urltemplate
            .parse(contractManagement._links.getDectOffer.href)
            .expand({ id: contractId })}
        >
          {children}
        </LoadDectOffer>
      );
  }
};

type LoadSpecificContractProps = {
  url: string;
  children: (contract: SpecificContract) => ReactNode;
};

const LoadFmct: FC<LoadSpecificContractProps> = ({ url, children }) => {
  const request = useFmct({ url });
  if (!request.response) return <PageSpinner />;
  return children({ type: 'fmct', request });
};

const LoadDect: FC<LoadSpecificContractProps> = ({ url, children }) => {
  const request = useDect({ url });
  if (!request.response) return <PageSpinner />;
  return children({ type: 'dect', request });
};

const LoadLect: FC<LoadSpecificContractProps> = ({ url, children }) => {
  const request = useLect({ url });
  if (!request.response) return <PageSpinner />;
  return children({ type: 'lect', request });
};

const LoadFmctOffer: FC<LoadSpecificContractProps> = ({ url, children }) => {
  const request = useFmctOffer({ url });
  if (!request.response) return <PageSpinner />;
  return children({ type: 'fmct-offer', request });
};

const LoadDectOffer: FC<LoadSpecificContractProps> = ({ url, children }) => {
  const request = useDectOffer({ url });
  if (!request.response) return <PageSpinner />;
  return children({ type: 'dect-offer', request });
};

const LoadRest: FC<{ contract: SpecificContract }> = ({ contract }) => {
  const pageParams = useAssignmentsDetailsPageParams();

  const financialSecuritiesParams = useMemoOne<FinancialSecuritiesParams>(
    () => ({
      offset: pageParams.financialSecurities.value.start,
      limit: pageParams.financialSecurities.value.pageSize,
    }),
    [
      pageParams.financialSecurities.value.start,
      pageParams.financialSecurities.value.pageSize,
    ]
  );
  const financialSecurities = useContractFinancialSecurities({
    url: contract.request.response.data._links.getFinancialSecurities?.href,
    params: financialSecuritiesParams,
  });

  const creditGraph = useOptionalCreditGraph({
    url: contract.request.response.data._links.getCreditGraph?.href,
  });

  const shortTermCreditLimitSummary = useOptionalShortTermCreditLimitSummary({
    url: contract.request.response.data._links.getShortTermCreditLimitSummary
      ?.href,
  });

  if (
    financialSecurities?.response === null ||
    creditGraph?.response === null ||
    shortTermCreditLimitSummary?.response === null
  )
    return <PageSpinner />;

  return (
    <Page
      contract={contract}
      financialSecurities={financialSecurities}
      creditGraph={creditGraph}
      shortTermCreditLimitSummary={shortTermCreditLimitSummary}
    />
  );
};
